import { supabase } from "./churchSupabaseClient";

const SERVICE_TIMES_TABLE = "church_service_times";

function isMissingRelationError(error) {
  const message = String(error?.message || "").toLowerCase();
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes("could not find the table") ||
    message.includes("schema cache") ||
    message.includes("does not exist")
  );
}

function normalizeServiceTime(row = {}) {
  return {
    id: row.id || "",
    accountId: row.account_id || row.accountId || "",
    name: row.name || "",
    time: String(row.time_value || row.time || "").slice(0, 5),
    sortOrder: Number(row.sort_order || row.sortOrder || 0),
    createdAt: row.created_at || "",
    updatedAt: row.updated_at || "",
  };
}

function getMissingTableMessage() {
  return "The church_service_times table is missing. Run the service times SQL in Supabase, then try again.";
}

function requireServiceTimeInput(accountId, values = {}) {
  const name = String(values.name || "").trim();
  const time = String(values.time || "").trim();

  if (!accountId) {
    throw new Error("Organization account is missing.");
  }

  if (!name) {
    throw new Error("Add a service name.");
  }

  if (!time) {
    throw new Error("Add a service time.");
  }

  return { name, time };
}

export async function loadChurchServiceTimes(accountId) {
  if (!accountId) return [];

  const { data, error } = await supabase
    .from(SERVICE_TIMES_TABLE)
    .select("id, account_id, name, time_value, sort_order, created_at, updated_at")
    .eq("account_id", accountId)
    .order("sort_order", { ascending: true })
    .order("time_value", { ascending: true });

  if (error) {
    if (isMissingRelationError(error)) {
      return [];
    }
    throw error;
  }

  return (data || []).map(normalizeServiceTime);
}

export async function createChurchServiceTime(accountId, values = {}, existingServiceTimes = []) {
  const { name, time } = requireServiceTimeInput(accountId, values);
  const nextSortOrder =
    Math.max(0, ...(existingServiceTimes || []).map((item) => Number(item.sortOrder || 0))) + 1;

  const { data, error } = await supabase
    .from(SERVICE_TIMES_TABLE)
    .insert({
      account_id: accountId,
      name,
      time_value: time,
      sort_order: nextSortOrder,
    })
    .select("id, account_id, name, time_value, sort_order, created_at, updated_at")
    .single();

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error(getMissingTableMessage());
    }
    throw error;
  }

  return normalizeServiceTime(data);
}

export async function updateChurchServiceTime(accountId, serviceTimeId, values = {}) {
  const { name, time } = requireServiceTimeInput(accountId, values);

  if (!serviceTimeId) {
    throw new Error("Service time is missing.");
  }

  const { data, error } = await supabase
    .from(SERVICE_TIMES_TABLE)
    .update({
      name,
      time_value: time,
      updated_at: new Date().toISOString(),
    })
    .eq("account_id", accountId)
    .eq("id", serviceTimeId)
    .select("id, account_id, name, time_value, sort_order, created_at, updated_at")
    .single();

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error(getMissingTableMessage());
    }
    throw error;
  }

  return normalizeServiceTime(data);
}

export async function deleteChurchServiceTime(accountId, serviceTimeId) {
  if (!accountId || !serviceTimeId) return;

  const { error } = await supabase
    .from(SERVICE_TIMES_TABLE)
    .delete()
    .eq("account_id", accountId)
    .eq("id", serviceTimeId);

  if (error) {
    if (isMissingRelationError(error)) {
      throw new Error(getMissingTableMessage());
    }
    throw error;
  }
}
